import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { StoryViewer, Big, Label, Sub, plural } from "@/components/wrapped/StoryViewer";
import { UsersThree, MapPin, Barbell, CheckCircle, Trophy, Crown } from "@phosphor-icons/react";

const BAR_COLORS = ["#4dd0e1", "#a970ff", "#ffb340", "#ff6ec7", "#00e676"];

export default function FamilyWrappedPage() {
  const [data, setData] = useState();
  useEffect(() => { api.get("/family/wrapped").then((r) => setData(r.data)).catch(() => setData(null)); }, []);

  const slides = useMemo(() => {
    if (!data) return [];
    const t = data.totals;
    const maxKm = Math.max(...data.members.map((m) => m.km), 1);
    const s = [];
    s.push({ id: "intro", bg: "radial-gradient(circle at 30% 20%, #1a2b6b 0%, #0b0d12 65%)", render: () => (
      <>
        <UsersThree size={56} weight="fill" className="text-[#4dabf5] mb-6" />
        <Label>Wrapped семьи «{data.family}»</Label>
        <Big color="#fff">{data.year}</Big>
        <Sub>Год всей семьи в цифрах: общие километры, тренировки и герой года. Листайте →</Sub>
      </>
    )});
    if (t.km > 0) s.push({ id: "km", bg: "radial-gradient(circle at 25% 30%, #0a4d6b 0%, #0b0d12 70%)", render: () => (
      <>
        <MapPin size={44} weight="fill" className="text-[#4dd0e1] mb-6" />
        <Label>Километры всей семьи</Label>
        <Big color="#4dd0e1">{t.km} км</Big>
        {t.compare && <Sub>Вместе вы прошли {t.compare}!</Sub>}
        <div className="mt-6 w-full max-w-sm space-y-2" data-testid="fw-km-bars">
          {data.members.filter((m) => m.km > 0).map((m, i) => (
            <div key={m.name} className="flex items-center gap-2 text-xs">
              <span className="w-20 text-right truncate opacity-80">{m.name}</span>
              <div className="flex-1 h-3 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(m.km / maxKm) * 100}%`, background: BAR_COLORS[i % 5] }} />
              </div>
              <b className="num w-16 text-left">{m.km} км</b>
            </div>
          ))}
        </div>
      </>
    )});
    if (t.workouts > 0 || t.sleep_h > 0) s.push({ id: "active", bg: "radial-gradient(circle at 70% 70%, #6b3a0a 0%, #0b0d12 70%)", render: () => (
      <>
        <Barbell size={44} weight="fill" className="text-[#ffb340] mb-6" />
        <Label>Активность семьи</Label>
        <Big color="#ffb340">{t.workouts}</Big>
        <Sub>{plural(t.workouts, ["тренировка", "тренировки", "тренировок"])} на всех за год.
          {t.sleep_h > 0 && <> Суммарно семья проспала <b className="num">{t.sleep_h.toLocaleString("ru-RU")} ч</b>.</>}
          {t.green_days > 0 && <> Зелёных дней восстановления — <b className="num">{t.green_days}</b>.</>}</Sub>
      </>
    )});
    if (t.habits > 0 || t.tasks > 0 || data.challenges_done > 0) s.push({ id: "discipline", bg: "radial-gradient(circle at 70% 30%, #4d0a3a 0%, #0b0d12 70%)", render: () => (
      <>
        <CheckCircle size={44} weight="fill" className="text-[#ff6ec7] mb-6" />
        <Label>Дисциплина семьи</Label>
        <Big color="#ff6ec7">{t.habits}</Big>
        <Sub>отметок привычек на всех.
          {t.tasks > 0 && <> Закрыто задач: <b className="num">{t.tasks}</b>.</>}
          {data.challenges_done > 0 && <> Выполнено семейных челленджей: <b className="num">{data.challenges_done}</b> 🎉</>}</Sub>
      </>
    )});
    if (data.hero) s.push({ id: "hero", bg: "radial-gradient(circle at 50% 30%, #4a3a0a 0%, #0b0d12 70%)", render: () => (
      <>
        <Crown size={48} weight="fill" className="text-amber-300 mb-6" />
        <Label>Герой года</Label>
        <div className="font-heading font-extrabold leading-none" style={{ fontSize: "clamp(2.8rem,9vw,5.5rem)", color: "#ffd750" }} data-testid="fw-hero-name">{data.hero.name}</div>
        <Sub>🏆 {data.hero.reason}. Аплодисменты!</Sub>
      </>
    )});
    s.push({ id: "final", share: true, bg: "radial-gradient(circle at 50% 15%, #1a2b6b 0%, #0b0d12 70%)", render: (finalRef) => (
      <div ref={finalRef} className="flex flex-col items-center text-center px-8 py-10 rounded-3xl"
        style={{ background: "radial-gradient(circle at 50% 0%, #14204d 0%, #0b0d12 80%)", border: "1px solid rgba(77,171,245,0.3)" }}>
        <UsersThree size={40} weight="fill" className="text-[#4dabf5] mb-3" />
        <div className="text-xs uppercase tracking-[0.25em] opacity-70 mb-3">Семья «{data.family}» · {data.year}</div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 my-2">
          {t.km > 0 && <div><div className="font-heading font-extrabold num text-3xl text-[#4dd0e1]">{t.km} км</div><div className="text-[11px] opacity-70">вместе</div></div>}
          {t.workouts > 0 && <div><div className="font-heading font-extrabold num text-3xl text-[#ffb340]">{t.workouts}</div><div className="text-[11px] opacity-70">тренировок</div></div>}
          {t.habits > 0 && <div><div className="font-heading font-extrabold num text-3xl text-[#ff6ec7]">{t.habits}</div><div className="text-[11px] opacity-70">{plural(t.habits, ["отметка привычек", "отметки привычек", "отметок привычек"])}</div></div>}
          {t.sleep_h > 0 && <div><div className="font-heading font-extrabold num text-3xl text-[#a970ff]">{t.sleep_h.toLocaleString("ru-RU")} ч</div><div className="text-[11px] opacity-70">сна на всех</div></div>}
        </div>
        {data.hero && <div className="mt-2 text-sm">🏆 Герой года — <b className="text-amber-300">{data.hero.name}</b></div>}
        <div className="text-[10px] opacity-50 mt-3">rukos.life</div>
      </div>
    )});
    return s;
  }, [data]);

  if (data === undefined) return <div className="glass-card h-40 animate-pulse mt-10" />;
  if (!data || !data.has_data) return (
    <div className="glass-card p-8 text-center mt-10" data-testid="fw-no-data">
      <Trophy size={40} className="text-muted-foreground mx-auto mb-3" />
      <p className="font-semibold mb-1">Пока нечего показывать</p>
      <p className="text-sm text-muted-foreground">Нужна семья с данными за год: Whoop, GPS-маршруты или привычки участников.</p>
    </div>
  );

  return <StoryViewer slides={slides} shareName={`lifeos-family-wrapped-${data.year}`} testPrefix="fw" />;
}
